import Image from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';

import Button, { ButtonSize, ButtonVariants } from '@/components/switchback/Button/Button';
import {
  ELeftSplitType,
  ERightSplitType,
  IRoadsideAssistanceProps,
  ISplitContentBlock,
} from '@/pages/roadside-assistance';

import css from './RoadsideAssistanceTemplate.module.css';

const SplitContentBlock: React.FC<ISplitContentBlock> = ({ left, right }) => {
  return (
    <div className={css.splitContentCard}>
      {left.map((section, index) => {
        switch (section.type) {
          case ELeftSplitType.image: {
            const { url, title: alt } = section.value;
            return (
              <div className={css.contentCardImg}>
                <Image src={url} alt={alt} key={`0${index}`} width={48} height={48} />
              </div>
            );
          }
          case ELeftSplitType.heading: {
            const { value: title } = section;
            return <h5 className={css.contentCardTitle}>{title}</h5>;
          }
          case ELeftSplitType.richText: {
            const { value: subTitle } = section;
            return (
              <div
                className={css.contentCardSubTitle}
                dangerouslySetInnerHTML={{ __html: subTitle }}
              />
            );
          }
          default:
            break;
        }
      })}
      {right.map((section, index) => {
        switch (section.type) {
          case ERightSplitType.richText: {
            const { value: description } = section;
            return (
              <div
                key={`1${index}`}
                className={css.contentCardList}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            );
          }
        }
      })}
    </div>
  );
};

const RoadsideAssistanceTemplate: React.FC<IRoadsideAssistanceProps> = ({
  heading,
  subheading,
  contentBlocks,
  heroImage,
}) => {
  const intl = useIntl();
  const renderSplit = (): JSX.Element[] => {
    const sections: JSX.Element[] = [];
    contentBlocks.forEach(({ type, value }) => {
      switch (type) {
        case 'split': {
          sections.push(<SplitContentBlock {...value} />);
          break;
        }
        default:
          break;
      }
    });
    return sections;
  };

  return (
    <main className={css.main}>
      <section className={css.hero} style={{ backgroundImage: `url(${heroImage})` }}>
        <div className={css.heroHeadings}>
          <h3 className={css.heroHeading}>{heading}</h3>
          <h6 className={css.heroSubHeading}>{subheading}</h6>
        </div>
      </section>
      <section className={css.splitContent}>{renderSplit()}</section>
      <section className={css.customerService}>
        <h4 className={css.customerServiceTitle}>
          {intl.formatMessage({
            defaultMessage: 'Still have questions? We love to help.',
            id: '5g8z18',
          })}
        </h4>
        <Button
          size={ButtonSize.small}
          variant={ButtonVariants.primaryOutlined}
          href={`${process.env.hostApp}/help/roadside-assistance`}>
          {intl.formatMessage({ defaultMessage: 'Learn More', id: '7DIW6+' })}
        </Button>
        <p className={css.customerServiceCall}>
          {intl.formatMessage({
            defaultMessage: 'Or call 877-723-7232 to connect with our service team',
            id: 'DKarSc',
          })}
        </p>
      </section>
    </main>
  );
};

export default RoadsideAssistanceTemplate;
